import { Button, Spinner, useTranslation } from '@hbs-us/ui';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import type { EmployeeDetailDto, EmployeePayType } from './contractsMirror.js';
import {
  useCreateEmployeeMutation,
  useEmployeeQuery,
  useUpdateEmployeeMutation,
} from './useEmployeeQuery.js';

/**
 * Create/edit employee form (legacy `AddEmployeePage` / `EditEmployeePage`), redesigned to
 * the locked brand as an archetype E grouped-section form and WIRED to the api-host write
 * endpoints (Plan 22-24): POST /api/v1/employees (create) + PATCH /api/v1/employees/:id (edit).
 *
 *  • Employee — name, contact (email, phone)
 *  • Pay & Eligibility — pay type (Hourly/Salary/Weekly) with the conditional rate field,
 *    overtime-eligible (Hourly only) + lunch-deduction-exempt toggles
 *  • Status — active flag (edit only)
 *
 * The server owns the pay-type validation (D-03) — the SPA sends the raw fields and surfaces the
 * server's error. No tenantId is ever sent (the server scopes to the principal, SEC-11). The primary
 * CTA carries its SPECIFIC label ("Save Employee" / "Save Changes") via t() (never a bare "Save",
 * A11Y-01). Every color is a locked token (BRAND-06).
 */

const SECTION = 'rounded-md border border-border bg-card p-6';
const FIELD_LABEL = 'font-medium text-foreground text-sm';
const INPUT =
  'mt-1 h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

const PAY_TYPES: readonly EmployeePayType[] = ['Hourly', 'Salary', 'Weekly'];

export interface EmployeeFormProps {
  mode: 'create' | 'edit';
}

const editRouteApi = getRouteApi('/employees/$id/edit');

interface FormState {
  name: string;
  email: string;
  phone: string;
  payType: EmployeePayType;
  hourlyRate: string;
  weeklySalary: string;
  annualSalary: string;
  overtimeEligible: boolean;
  lunchDeductionExempt: boolean;
  active: boolean;
}

function initialStateFrom(employee?: EmployeeDetailDto): FormState {
  return {
    name: employee?.name ?? '',
    email: employee?.email ?? '',
    phone: employee?.phone ?? '',
    payType: employee?.payType ?? 'Hourly',
    hourlyRate: employee?.hourlyRate != null ? String(employee.hourlyRate) : '',
    weeklySalary: employee?.weeklySalary != null ? String(employee.weeklySalary) : '',
    annualSalary: employee?.annualSalary != null ? String(employee.annualSalary) : '',
    overtimeEligible: Number(employee?.overtimeEligible ?? 0) === 1,
    lunchDeductionExempt: Number(employee?.lunchDeductionExempt ?? 0) === 1,
    active: employee ? Number(employee.active ?? 1) === 1 : true,
  };
}

function EmployeeFormFields({
  mode,
  employeeId,
  initial,
}: {
  mode: 'create' | 'edit';
  employeeId?: number;
  initial: FormState;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMutation = useCreateEmployeeMutation();
  const updateMutation = useUpdateEmployeeMutation(employeeId ?? 0);
  const mutation = mode === 'create' ? createMutation : updateMutation;

  const [form, setForm] = useState<FormState>(initial);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const submitKey = mode === 'create' ? 'employees:form.submitCreate' : 'employees:form.submitEdit';

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const payload = {
      name: form.name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      payType: form.payType,
      hourlyRate: form.hourlyRate ? Number(form.hourlyRate) : 0,
      weeklySalary: form.weeklySalary ? Number(form.weeklySalary) : 0,
      annualSalary: form.annualSalary ? Number(form.annualSalary) : 0,
      overtimeEligible: form.overtimeEligible,
      lunchDeductionExempt: form.lunchDeductionExempt,
      ...(mode === 'edit' ? { active: form.active } : {}),
    };
    mutation.mutate(payload, { onSuccess: () => void navigate({ to: '/employees' }) });
  }

  return (
    <form className="max-w-2xl space-y-4" onSubmit={onSubmit}>
      <section className={SECTION}>
        <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
          {t('employees:form.employeeHeading')}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="emp-name" className={FIELD_LABEL}>
              {t('employees:form.name')}
            </label>
            <input
              id="emp-name"
              className={INPUT}
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="emp-email" className={FIELD_LABEL}>
              {t('employees:form.email')}
            </label>
            <input
              id="emp-email"
              type="email"
              className={INPUT}
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="emp-phone" className={FIELD_LABEL}>
              {t('employees:form.phone')}
            </label>
            <input
              id="emp-phone"
              className={INPUT}
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
            />
          </div>
        </div>
      </section>

      <section className={SECTION}>
        <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
          {t('employees:form.payHeading')}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="emp-pay-type" className={FIELD_LABEL}>
              {t('employees:form.payType')}
            </label>
            <select
              id="emp-pay-type"
              className={INPUT}
              value={form.payType}
              onChange={(e) => set('payType', e.target.value as EmployeePayType)}
            >
              {PAY_TYPES.map((pt) => (
                <option key={pt} value={pt}>
                  {t(`employees:payType.${pt}`)}
                </option>
              ))}
            </select>
          </div>

          {form.payType === 'Hourly' ? (
            <div>
              <label htmlFor="emp-hourly" className={FIELD_LABEL}>
                {t('employees:form.hourlyRate')}
              </label>
              <input
                id="emp-hourly"
                type="number"
                min="0"
                step="0.01"
                className={INPUT}
                value={form.hourlyRate}
                onChange={(e) => set('hourlyRate', e.target.value)}
              />
            </div>
          ) : null}
          {form.payType === 'Salary' ? (
            <div>
              <label htmlFor="emp-annual" className={FIELD_LABEL}>
                {t('employees:form.annualSalary')}
              </label>
              <input
                id="emp-annual"
                type="number"
                min="0"
                step="0.01"
                className={INPUT}
                value={form.annualSalary}
                onChange={(e) => set('annualSalary', e.target.value)}
              />
            </div>
          ) : null}
          {form.payType === 'Weekly' ? (
            <div>
              <label htmlFor="emp-weekly" className={FIELD_LABEL}>
                {t('employees:form.weeklySalary')}
              </label>
              <input
                id="emp-weekly"
                type="number"
                min="0"
                step="0.01"
                className={INPUT}
                value={form.weeklySalary}
                onChange={(e) => set('weeklySalary', e.target.value)}
              />
            </div>
          ) : null}
        </div>

        <div className="mt-4 space-y-2">
          {form.payType === 'Hourly' ? (
            <label className="flex items-center gap-2 text-foreground text-sm">
              <input
                type="checkbox"
                checked={form.overtimeEligible}
                onChange={(e) => set('overtimeEligible', e.target.checked)}
              />
              {t('employees:form.overtimeEligible')}
            </label>
          ) : null}
          <label className="flex items-center gap-2 text-foreground text-sm">
            <input
              type="checkbox"
              checked={form.lunchDeductionExempt}
              onChange={(e) => set('lunchDeductionExempt', e.target.checked)}
            />
            {t('employees:form.lunchDeductionExempt')}
          </label>
        </div>
      </section>

      {mode === 'edit' ? (
        <section className={SECTION}>
          <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
            {t('employees:form.statusHeading')}
          </h2>
          <label className="flex items-center gap-2 text-foreground text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => set('active', e.target.checked)}
            />
            {t('employees:form.active')}
          </label>
        </section>
      ) : null}

      {mutation.isError ? (
        <p role="alert" aria-live="assertive" className="text-destructive text-sm">
          {t('employees:form.saveError')}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" disabled={mutation.isPending}>
          {t(submitKey)}
        </Button>
        <Button asChild variant="secondary">
          <Link to="/employees">{t('employees:form.cancel')}</Link>
        </Button>
      </div>
    </form>
  );
}

function EditEmployeeForm() {
  const { t } = useTranslation();
  const { id } = editRouteApi.useParams();
  const employeeId = Number(id);
  const query = useEmployeeQuery(employeeId);

  if (query.isPending) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }
  if (query.isError || !query.data.employee) {
    return (
      <section role="alert" aria-live="assertive" className={SECTION}>
        <p className="font-medium text-foreground text-sm">{t('employees:error.detailHeading')}</p>
        <p className="mt-2 text-muted-foreground text-sm">{t('employees:error.detailBody')}</p>
      </section>
    );
  }

  return (
    <EmployeeFormFields
      mode="edit"
      employeeId={employeeId}
      initial={initialStateFrom(query.data.employee)}
    />
  );
}

export function EmployeeForm({ mode }: EmployeeFormProps) {
  const { t } = useTranslation();
  const headingKey =
    mode === 'create' ? 'employees:form.createHeading' : 'employees:form.editHeading';

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display font-semibold text-3xl text-foreground">{t(headingKey)}</h1>
      </header>
      {mode === 'create' ? (
        <EmployeeFormFields mode="create" initial={initialStateFrom()} />
      ) : (
        <EditEmployeeForm />
      )}
    </div>
  );
}
